// Every path of the payments area, in one place.
//
// The configuration screens used to live under a base of their own
// (/payment-configurations/…) while the tabs sat under /payments-account/…,
// which made two unrelated-looking halves of a single feature. They are one
// tree now, and the paths are built here rather than written as literals at a
// dozen call sites.

export const DASHBOARD_PATH = "/";

const BASE = "/payments-account";

/** The tabs of the payments area, in the order they are shown. */
export const PAYMENTS_TABS = [
  "resume",
  "payments_history",
  "billing",
  "commissions",
  "configurations"
] as const;

export type PaymentsTab = typeof PAYMENTS_TABS[number];

export const paymentsTabPath = (tab: PaymentsTab) => `${BASE}/${tab}`;

/** Where the navigation into the payments area lands by default. */
export const PAYMENTS_HOME_PATH = paymentsTabPath("resume");

export const CONFIGURATIONS_PATH = paymentsTabPath("configurations");

/**
 * What the payments service is, shown before anything is filled in.
 *
 * Only reached by a professional with no account at all: it is the first thing
 * they see instead of a summary of nothing, and it is not shown again once an
 * account exists, however incomplete.
 */
export const PAYMENTS_WELCOME_PATH = `${BASE}/welcome`;

export const NEW_CONFIGURATION_PATH = `${BASE}/configurations/new`;

export const configurationPath = (configId: string) =>
  `${BASE}/configurations/${configId}`;

export const configurationNextStepsPath = (configId: string) =>
  `${configurationPath(configId)}/next-steps`;

export const configurationDocumentsPath = (configId: string) =>
  `${configurationPath(configId)}/documents`;

/** Where confirming the documents lands, while Easypay reviews them. */
export const configurationInReviewPath = (configId: string) =>
  `${configurationPath(configId)}/in-review`;

// Route patterns, for the router itself. The literal paths above have to be
// declared before `PAYMENTS_TAB_ROUTE`, and that one has to be exact, or
// "/payments-account/configurations/new" would be read as the tab named
// "configurations".
export const PAYMENTS_WELCOME_ROUTE = PAYMENTS_WELCOME_PATH;
export const NEW_CONFIGURATION_ROUTE = NEW_CONFIGURATION_PATH;
export const CONFIGURATION_NEXT_STEPS_ROUTE = `${BASE}/configurations/:configId/next-steps`;
export const CONFIGURATION_DOCUMENTS_ROUTE = `${BASE}/configurations/:configId/documents`;
export const CONFIGURATION_IN_REVIEW_ROUTE = `${BASE}/configurations/:configId/in-review`;
export const CONFIGURATION_ROUTE = `${BASE}/configurations/:configId`;
export const PAYMENTS_TAB_ROUTE = `${BASE}/:type`;
